/**
 * RecommendationCard - Action Recommendation Display Component
 * 
 * Why this matters: Presents prioritized recommendations with clear actions,
 * reasoning, and product links to Dr. Mani's Magic products.
 */

import React from 'react';
import { ArrowRight, Droplets, Bug, Leaf, ExternalLink } from 'lucide-react';
import { DiagnosisRecommendation } from './CitrusDoctor';

interface RecommendationCardProps {
  recommendation: DiagnosisRecommendation;
  index: number;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({ recommendation, index }) => {
  /**
   * Get pillar category info
   */
  const getPillarInfo = () => {
    switch (recommendation.threePillarsCategory) {
      case 'microbes':
        return { 
          icon: Bug, 
          color: 'pillar-microbes', 
          label: 'Pillar 1: Microbes',
          description: 'Live Bacteria & Fungi'
        };
      case 'nutrition':
        return { 
          icon: Leaf, 
          color: 'pillar-nutrition', 
          label: 'Pillar 2: Nutrition',
          description: 'Organic Fertilizer'
        };
      case 'soil':
        return { 
          icon: Droplets, 
          color: 'pillar-soil', 
          label: 'Pillar 3: Soil',
          description: 'Mineral-Based Soil'
        };
      default:
        return null;
    }
  };

  const pillarInfo = getPillarInfo();
  const PillarIcon = pillarInfo?.icon;
  const product = recommendation.productRecommendation;

  return (
    <div className={`recommendation-card priority-${recommendation.priority}`}>
      {/* Priority Badge */}
      <div className="recommendation-header">
        <div className="recommendation-priority">
          <span className="priority-number">{recommendation.priority}</span>
          <span className="priority-label">Priority</span>
        </div>
        
        {pillarInfo && (
          <div className={`pillar-badge ${pillarInfo.color}`}>
            {PillarIcon && <PillarIcon size={14} />}
            <span>{pillarInfo.label}</span>
          </div>
        )}
      </div>

      {/* Action */}
      <div className="recommendation-action">
        <ArrowRight size={18} className="action-icon" />
        <h4 className="action-text">{recommendation.action}</h4>
      </div>

      {/* Reasoning */}
      {recommendation.reasoning && (
        <p className="recommendation-reasoning">
          <strong>Why:</strong> {recommendation.reasoning}
        </p>
      )}

      {/* Product Recommendation */}
      {product && (
        <div className="recommendation-product">
          <div className="product-info">
            <span className="product-label">Recommended Product</span>
            <h5 className="product-name">{product.name}</h5>
            {product.whyItHelps && (
              <p className="product-benefit">{product.whyItHelps}</p>
            )}
          </div>
          
          {product.productUrl && (
            <a 
              href={product.productUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="product-link"
            >
              <span>View Product</span>
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default RecommendationCard;
